/**
 * Validated Resource Form
 * 
 * Production-ready form for resource allocation and capacity management
 */

import {
    Alert,
    Badge,
    Button,
    Card,
    Checkbox,
    Group,
    JsonInput,
    NumberInput,
    Paper,
    Progress,
    Select,
    Stack,
    Text,
    Textarea,
    TextInput,
    Title,
    Tabs,
    Grid,
    RingProgress,
    Table
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
    IconCheck,
    IconAlertTriangle,
    IconInfoCircle,
    IconX,
    IconServer,
    IconGauge,
    IconUsers,
    IconClock
} from '@tabler/icons-react';
import React, { useState } from 'react';
import { useValidatedForm, useValidatedFormSubmission } from '../hooks/useValidatedForm';
import { useResourceMutations } from '../data/hooks/useResource';

interface ResourceCapacity {
    cpu: {
        total: number;
        allocated: number;
        reserved: number;
        unit: 'cores' | 'vcores';
    };
    memory: {
        total: number;
        allocated: number;
        reserved: number;
        unit: 'GB' | 'MB';
    };
    storage: {
        total: number;
        allocated: number;
        reserved: number;
        unit: 'GB' | 'TB';
    };
    network: {
        bandwidth: number;
        unit: 'Mbps' | 'Gbps';
    };
}

interface ResourceConstraints {
    maxConcurrentTasks: number;
    maxQueueSize: number;
    allowOvercommit: boolean;
    priorityLevels: string[];
    maintenanceWindows: Array<{
        day: string;
        startTime: string;
        endTime: string;
    }>;
}

interface ResourceData {
    name: string;
    description?: string;
    type: 'compute' | 'storage' | 'network' | 'database' | 'queue' | 'custom';
    provider: 'aws' | 'azure' | 'gcp' | 'on_premise' | 'hybrid';
    location: string;
    capacity: ResourceCapacity;
    constraints: ResourceConstraints;
    costPerHour?: number;
    currency?: string;
    tags: string[];
    healthCheck: {
        enabled: boolean;
        endpoint?: string;
        intervalSeconds?: number;
        timeoutSeconds?: number;
    };
    monitoring: {
        enabled: boolean;
        metricsEndpoint?: string;
        alertThresholds?: {
            cpuPercent: number;
            memoryPercent: number;
            storagePercent: number;
        };
    };
    enabled: boolean;
    metadata?: Record<string, any>;
}

interface ValidatedResourceFormProps {
    initialData?: Partial<ResourceData>;
    mode: 'create' | 'edit';
    onSuccess?: (data: ResourceData) => void;
    onCancel?: () => void;
}

export const ValidatedResourceForm: React.FC<ValidatedResourceFormProps> = ({
    initialData,
    mode,
    onSuccess,
    onCancel
}) => {
    const { createResource, updateResource } = useResourceMutations();
    const [activeTab, setActiveTab] = useState<string | null>('basic');

    // Initialize validated form
    const form = useValidatedForm<Partial<ResourceData>>({
        schemaName: 'Resource' as any,
        initialValues: {
            name: initialData?.name || '',
            description: initialData?.description || '',
            type: initialData?.type || 'compute',
            provider: initialData?.provider || 'on_premise',
            location: initialData?.location || '',
            capacity: initialData?.capacity || {
                cpu: { total: 8, allocated: 0, reserved: 0, unit: 'cores' },
                memory: { total: 32, allocated: 0, reserved: 0, unit: 'GB' },
                storage: { total: 500, allocated: 0, reserved: 0, unit: 'GB' },
                network: { bandwidth: 1000, unit: 'Mbps' }
            },
            constraints: initialData?.constraints || {
                maxConcurrentTasks: 10,
                maxQueueSize: 100,
                allowOvercommit: false,
                priorityLevels: ['low', 'normal', 'high', 'urgent'],
                maintenanceWindows: []
            },
            costPerHour: initialData?.costPerHour || undefined,
            currency: initialData?.currency || 'USD',
            tags: initialData?.tags || [],
            healthCheck: initialData?.healthCheck || {
                enabled: true,
                endpoint: '/health',
                intervalSeconds: 60,
                timeoutSeconds: 10
            },
            monitoring: initialData?.monitoring || {
                enabled: true,
                alertThresholds: {
                    cpuPercent: 80,
                    memoryPercent: 85,
                    storagePercent: 90
                }
            },
            enabled: initialData?.enabled !== undefined ? initialData.enabled : true,
            metadata: initialData?.metadata || {}
        },
        validateOnChange: true,
        validateOnBlur: true,
        showNotifications: false
    });

    // Form submission with GraphQL
    const { submitForm, loading: submitting, submissionResult } = useValidatedFormSubmission(
        'Resource' as any,
        () => ({
            mutate: async (data: Partial<ResourceData>) => {
                if (mode === 'create') {
                    return await createResource(data as any);
                } else {
                    return await updateResource(initialData?.name || '', data as any);
                }
            },
            loading: false,
            error: null
        })
    );

    // Resource type options
    const typeOptions = [
        { value: 'compute', label: 'Compute - CPU and memory resources' },
        { value: 'storage', label: 'Storage - Disk and data storage' },
        { value: 'network', label: 'Network - Bandwidth and connectivity' },
        { value: 'database', label: 'Database - Database connections and capacity' },
        { value: 'queue', label: 'Queue - Message queue and processing capacity' },
        { value: 'custom', label: 'Custom - Custom resource type' }
    ];

    // Provider options
    const providerOptions = [
        { value: 'aws', label: 'Amazon Web Services (AWS)' },
        { value: 'azure', label: 'Microsoft Azure' },
        { value: 'gcp', label: 'Google Cloud Platform (GCP)' },
        { value: 'on_premise', label: 'On-Premise Infrastructure' },
        { value: 'hybrid', label: 'Hybrid Cloud Setup' }
    ];

    // Currency options
    const currencyOptions = [
        { value: 'USD', label: 'US Dollar (USD)' },
        { value: 'EUR', label: 'Euro (EUR)' },
        { value: 'GBP', label: 'British Pound (GBP)' },
        { value: 'CAD', label: 'Canadian Dollar (CAD)' },
        { value: 'AUD', label: 'Australian Dollar (AUD)' }
    ];

    // Days of week for maintenance windows
    const daysOfWeek = [
        'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
    ];

    // Handle form submission
    const handleSubmit = async (values: Record<string, unknown>) => {
        const result = await submitForm(values);
        if (result.success && onSuccess) {
            onSuccess(result.data);
        }
    };

    // Calculate utilization percentages
    const getUtilizationPercent = (allocated: number, total: number) => {
        return total > 0 ? Math.round((allocated / total) * 100) : 0;
    };

    // Get utilization color
    const getUtilizationColor = (percent: number) => {
        if (percent >= 90) return 'red';
        if (percent >= 70) return 'orange';
        if (percent >= 50) return 'yellow';
        return 'green';
    };

    // Add maintenance window
    const addMaintenanceWindow = () => {
        const currentWindows = form.values.constraints?.maintenanceWindows || [];
        const newWindow = {
            day: 'Sunday',
            startTime: '02:00',
            endTime: '04:00'
        };
        form.setFieldValue('constraints.maintenanceWindows', [...currentWindows, newWindow]);
    };

    // Remove maintenance window
    const removeMaintenanceWindow = (index: number) => {
        const currentWindows = form.values.constraints?.maintenanceWindows || [];
        const updatedWindows = currentWindows.filter((_: any, i: number) => i !== index);
        form.setFieldValue('constraints.maintenanceWindows', updatedWindows);
    };

    // Update maintenance window
    const updateMaintenanceWindow = (index: number, field: string, value: string) => {
        const currentWindows = [...(form.values.constraints?.maintenanceWindows || [])];
        currentWindows[index] = { ...currentWindows[index], [field]: value };
        form.setFieldValue('constraints.maintenanceWindows', currentWindows);
    };

    return (
        <form onSubmit={form.onSubmit((values) => handleSubmit(values as Record<string, unknown>))}>
            <Stack gap="lg">
                {/* Header */}
                <Paper p="md" withBorder>
                    <Group justify="space-between">
                        <div>
                            <Group>
                                <Title order={3}>
                                    {mode === 'create' ? 'Create' : 'Edit'} Resource
                                </Title>
                                <IconServer size={24} color="blue" />
                            </Group>
                            <Text size="sm" c="dimmed">
                                Configure resource allocation and capacity management
                            </Text>
                        </div>
                        <Group>
                            <Badge 
                                color={form.validationResult.valid ? 'green' : 'red'}
                                leftSection={form.validationResult.valid ? <IconCheck size={14} /> : <IconX size={14} />}
                            >
                                {form.validationResult.valid ? 'Valid' : `${form.validationResult.errors.length} Error${form.validationResult.errors.length > 1 ? 's' : ''}`}
                            </Badge>
                        </Group>
                    </Group>
                </Paper>

                {/* Validation Summary */}
                {form.validationResult.errors.length > 0 && (
                    <Alert color="red" title="Validation Errors" icon={<IconAlertTriangle />}>
                        <Stack gap="xs">
                            {form.validationResult.errors.map((error, index) => (
                                <Text key={index} size="sm">
                                    <strong>{error.field}:</strong> {error.message}
                                </Text>
                            ))}
                        </Stack>
                    </Alert>
                )}

                {form.validationResult.warnings && form.validationResult.warnings.length > 0 && (
                    <Alert color="yellow" title="Validation Warnings" icon={<IconInfoCircle />}>
                        <Stack gap="xs">
                            {form.validationResult.warnings.map((warning, index) => (
                                <Text key={index} size="sm">
                                    <strong>{warning.field}:</strong> {warning.message}
                                    {warning.suggestion && <Text c="dimmed"> • {warning.suggestion}</Text>}
                                </Text>
                            ))}
                        </Stack>
                    </Alert>
                )}

                {/* Resource Form Tabs */}
                <Tabs value={activeTab} onChange={(v) => setActiveTab(v ?? '')}>
                    <Tabs.List>
                        <Tabs.Tab value="basic" leftSection={<IconInfoCircle size={16} />}>
                            Basic Info
                        </Tabs.Tab>
                        <Tabs.Tab value="capacity" leftSection={<IconGauge size={16} />}>
                            Capacity
                        </Tabs.Tab>
                        <Tabs.Tab value="constraints" leftSection={<IconUsers size={16} />}>
                            Constraints
                        </Tabs.Tab>
                        <Tabs.Tab value="monitoring" leftSection={<IconClock size={16} />}>
                            Monitoring
                        </Tabs.Tab>
                    </Tabs.List>

                    {/* Basic Information Tab */}
                    <Tabs.Panel value="basic">
                        <Card withBorder mt="md">
                            <Stack gap="md">
                                <TextInput
                                    label="Resource Name"
                                    placeholder="Production Compute Cluster"
                                    required
                                    {...form.getInputProps('name')}
                                    error={form.getFieldError('name')}
                                    rightSection={
                                        form.isFieldValid('name') && form.values.name ? 
                                        <IconCheck size={16} color="green" /> : undefined
                                    }
                                />

                                <Textarea
                                    label="Description"
                                    placeholder="High-performance compute cluster for production workloads..."
                                    minRows={3}
                                    {...form.getInputProps('description')}
                                    error={form.getFieldError('description')}
                                />

                                <Group grow>
                                    <Select
                                        label="Resource Type"
                                        data={typeOptions}
                                        required
                                        {...form.getInputProps('type')}
                                        error={form.getFieldError('type')}
                                    />

                                    <Select
                                        label="Provider"
                                        data={providerOptions}
                                        required
                                        {...form.getInputProps('provider')}
                                        error={form.getFieldError('provider')}
                                    />
                                </Group>

                                <Group grow>
                                    <TextInput
                                        label="Location"
                                        placeholder="us-east-1 / Data Center A"
                                        required
                                        {...form.getInputProps('location')}
                                        error={form.getFieldError('location')}
                                    />

                                    <Checkbox
                                        label="Resource Enabled"
                                        description="Enable this resource for allocation"
                                        {...form.getInputProps('enabled', { type: 'checkbox' })}
                                    />
                                </Group>

                                <Group grow>
                                    <NumberInput
                                        label="Cost Per Hour"
                                        placeholder="0.50"
                                        decimalScale={2}
                                        min={0}
                                        {...form.getInputProps('costPerHour')}
                                        error={form.getFieldError('costPerHour')}
                                    />

                                    <Select
                                        label="Currency"
                                        data={currencyOptions}
                                        {...form.getInputProps('currency')}
                                        error={form.getFieldError('currency')}
                                    />
                                </Group>

                                <TextInput
                                    label="Tags (comma-separated)"
                                    placeholder="production, compute, high-memory"
                                    value={form.values.tags?.join(', ') || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('tags',
                                        e.currentTarget.value.split(',').map((tag: string) => tag.trim()).filter(Boolean)
                                    )}
                                />
                            </Stack>
                        </Card>
                    </Tabs.Panel>

                    {/* Capacity Tab */}
                    <Tabs.Panel value="capacity">
                        <Card withBorder mt="md">
                            <Stack gap="md">
                                <Title order={4}>Resource Capacity</Title>

                                {/* CPU Capacity */}
                                <Card withBorder p="md">
                                    <Group justify="space-between" mb="md">
                                        <Text fw={500}>CPU Resources</Text>
                                        <RingProgress
                                            size={60}
                                            thickness={8}
                                            sections={[{
                                                value: getUtilizationPercent(
                                                    form.values.capacity?.cpu?.allocated || 0,
                                                    form.values.capacity?.cpu?.total || 1
                                                ),
                                                color: getUtilizationColor(getUtilizationPercent(
                                                    form.values.capacity?.cpu?.allocated || 0,
                                                    form.values.capacity?.cpu?.total || 1
                                                ))
                                            }]}
                                            label={
                                                <Text size="xs" ta="center">
                                                    {getUtilizationPercent(
                                                        form.values.capacity?.cpu?.allocated || 0,
                                                        form.values.capacity?.cpu?.total || 1
                                                    )}%
                                                </Text>
                                            }
                                        />
                                    </Group>
                                    <Grid>
                                        <Grid.Col span={3}>
                                            <NumberInput
                                                label="Total"
                                                min={0}
                                                value={form.values.capacity?.cpu?.total}
                                                onChange={(value: string | number | undefined) => form.setFieldValue('capacity.cpu.total', value)}
                                            />
                                        </Grid.Col>
                                        <Grid.Col span={3}>
                                            <NumberInput
                                                label="Allocated"
                                                min={0}
                                                value={form.values.capacity?.cpu?.allocated}
                                                onChange={(value: string | number | undefined) => form.setFieldValue('capacity.cpu.allocated', value)}
                                            />
                                        </Grid.Col>
                                        <Grid.Col span={3}>
                                            <NumberInput
                                                label="Reserved"
                                                min={0}
                                                value={form.values.capacity?.cpu?.reserved}
                                                onChange={(value: string | number | undefined) => form.setFieldValue('capacity.cpu.reserved', value)}
                                            />
                                        </Grid.Col>
                                        <Grid.Col span={3}>
                                            <Select
                                                label="Unit"
                                                data={[
                                                    { value: 'cores', label: 'Physical Cores' },
                                                    { value: 'vcores', label: 'Virtual Cores' }
                                                ]}
                                                value={form.values.capacity?.cpu?.unit}
                                                onChange={(value: string | null) => form.setFieldValue('capacity.cpu.unit', value ?? '')}
                                            />
                                        </Grid.Col>
                                    </Grid>
                                </Card>

                                {/* Memory Capacity */}
                                <Card withBorder p="md">
                                    <Group justify="space-between" mb="md">
                                        <Text fw={500}>Memory Resources</Text>
                                        <RingProgress
                                            size={60}
                                            thickness={8}
                                            sections={[{
                                                value: getUtilizationPercent(
                                                    form.values.capacity?.memory?.allocated || 0,
                                                    form.values.capacity?.memory?.total || 1
                                                ),
                                                color: getUtilizationColor(getUtilizationPercent(
                                                    form.values.capacity?.memory?.allocated || 0,
                                                    form.values.capacity?.memory?.total || 1
                                                ))
                                            }]}
                                            label={
                                                <Text size="xs" ta="center">
                                                    {getUtilizationPercent(
                                                        form.values.capacity?.memory?.allocated || 0,
                                                        form.values.capacity?.memory?.total || 1
                                                    )}%
                                                </Text>
                                            }
                                        />
                                    </Group>
                                    <Grid>
                                        <Grid.Col span={3}>
                                            <NumberInput
                                                label="Total"
                                                min={0}
                                                value={form.values.capacity?.memory?.total}
                                                onChange={(value: string | number) => form.setFieldValue('capacity.memory.total', value)}
                                            />
                                        </Grid.Col>
                                        <Grid.Col span={3}>
                                            <NumberInput
                                                label="Allocated"
                                                min={0}
                                                value={form.values.capacity?.memory?.allocated}
                                                onChange={(value: string | number) => form.setFieldValue('capacity.memory.allocated', value)}
                                            />
                                        </Grid.Col>
                                        <Grid.Col span={3}>
                                            <NumberInput
                                                label="Reserved"
                                                min={0}
                                                value={form.values.capacity?.memory?.reserved}
                                                onChange={(value: string | number) => form.setFieldValue('capacity.memory.reserved', value)}
                                            />
                                        </Grid.Col>
                                        <Grid.Col span={3}>
                                            <Select
                                                label="Unit"
                                                data={[
                                                    { value: 'GB', label: 'Gigabytes (GB)' },
                                                    { value: 'MB', label: 'Megabytes (MB)' }
                                                ]}
                                                value={form.values.capacity?.memory?.unit}
                                                onChange={(value: string | null) => form.setFieldValue('capacity.memory.unit', value ?? '')}
                                            />
                                        </Grid.Col>
                                    </Grid>
                                </Card>

                                {/* Storage & Network */}
                                <Group grow>
                                    <NumberInput
                                        label="Network Bandwidth"
                                        min={0}
                                        value={form.values.capacity?.network?.bandwidth}
                                        onChange={(value: string | number) => form.setFieldValue('capacity.network.bandwidth', value)}
                                    />
                                    
                                    <Select
                                        label="Bandwidth Unit"
                                        data={[
                                            { value: 'Mbps', label: 'Mbps' },
                                            { value: 'Gbps', label: 'Gbps' }
                                        ]}
                                        value={form.values.capacity?.network?.unit}
                                        onChange={(value: string | null) => form.setFieldValue('capacity.network.unit', value ?? '')}
                                    />
                                </Group>
                            </Stack>
                        </Card>
                    </Tabs.Panel>

                    {/* Constraints Tab */}
                    <Tabs.Panel value="constraints">
                        <Card withBorder mt="md">
                            <Stack gap="md">
                                <Title order={4}>Resource Constraints</Title>
                                
                                <Grid>
                                    <Grid.Col span={6}>
                                        <NumberInput
                                            label="Max Concurrent Tasks"
                                            min={1}
                                            value={form.values.constraints?.maxConcurrentTasks}
                                            onChange={(value: string | number | undefined) => form.setFieldValue('constraints.maxConcurrentTasks', value)}
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={6}>
                                        <NumberInput
                                            label="Max Queue Size"
                                            min={0}
                                            value={form.values.constraints?.maxQueueSize}
                                            onChange={(value: string | number | undefined) => form.setFieldValue('constraints.maxQueueSize', value)}
                                        />
                                    </Grid.Col>
                                </Grid>

                                <Checkbox
                                    label="Allow Overcommitment"
                                    description="Allow resource allocation beyond physical capacity"
                                    checked={form.values.constraints?.allowOvercommit || false}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('constraints.allowOvercommit', e.currentTarget.checked)}
                                />

                                <TextInput
                                    label="Priority Levels (comma-separated)"
                                    value={form.values.constraints?.priorityLevels?.join(', ') || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('constraints.priorityLevels',
                                        e.currentTarget.value.split(',').map((level: string) => level.trim()).filter(Boolean)
                                    )}
                                />

                                <Title order={5} mt="md">Maintenance Windows</Title>
                                <Group justify="flex-end" mb="md">
                                    <Button size="sm" variant="light" onClick={addMaintenanceWindow}>
                                        Add Window
                                    </Button>
                                </Group>

                                {form.values.constraints?.maintenanceWindows && form.values.constraints.maintenanceWindows.length > 0 ? (
                                    <Table>
                                        <Table.Thead>
                                            <Table.Tr>
                                                <Table.Th>Day</Table.Th>
                                                <Table.Th>Start Time</Table.Th>
                                                <Table.Th>End Time</Table.Th>
                                                <Table.Th>Actions</Table.Th>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {form.values.constraints.maintenanceWindows.map((window: any, index: number) => (
                                                <Table.Tr key={index}>
                                                    <Table.Td>
                                                        <Select
                                                            data={daysOfWeek.map(day => ({ value: day, label: day }))}
                                                            value={window.day}
                                                            onChange={(value: string | null) => updateMaintenanceWindow(index, 'day', value ?? '')}
                                                        />
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <TextInput
                                                            type="time"
                                                            value={window.startTime}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateMaintenanceWindow(index, 'startTime', e.currentTarget.value)}
                                                        />
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <TextInput
                                                            type="time"
                                                            value={window.endTime}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateMaintenanceWindow(index, 'endTime', e.currentTarget.value)}
                                                        />
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Button
                                                            size="xs"
                                                            variant="light"
                                                            color="red"
                                                            onClick={() => removeMaintenanceWindow(index)}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </Table.Td>
                                                </Table.Tr>
                                            ))}
                                        </Table.Tbody>
                                    </Table>
                                ) : (
                                    <Text c="dimmed" ta="center" py="md">
                                        No maintenance windows configured
                                    </Text>
                                )}
                            </Stack>
                        </Card>
                    </Tabs.Panel>

                    {/* Monitoring Tab */}
                    <Tabs.Panel value="monitoring">
                        <Card withBorder mt="md">
                            <Stack gap="md">
                                <Title order={4}>Health Check Configuration</Title>
                                <Checkbox
                                    label="Enable Health Checks"
                                    checked={form.values.healthCheck?.enabled || false}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('healthCheck.enabled', e.currentTarget.checked)}
                                />

                                {form.values.healthCheck?.enabled && (
                                    <Grid>
                                        <Grid.Col span={6}>
                                            <TextInput
                                                label="Health Check Endpoint"
                                                value={form.values.healthCheck.endpoint || ''}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('healthCheck.endpoint', e.currentTarget.value)}
                                            />
                                        </Grid.Col>
                                        <Grid.Col span={3}>
                                            <NumberInput
                                                label="Interval (seconds)"
                                                min={10}
                                                value={form.values.healthCheck.intervalSeconds}
                                                onChange={(value: string | number | undefined) => form.setFieldValue('healthCheck.intervalSeconds', value)}
                                            />
                                        </Grid.Col>
                                        <Grid.Col span={3}>
                                            <NumberInput
                                                label="Timeout (seconds)"
                                                min={1}
                                                value={form.values.healthCheck.timeoutSeconds}
                                                onChange={(value: string | number | undefined) => form.setFieldValue('healthCheck.timeoutSeconds', value)}
                                            />
                                        </Grid.Col>
                                    </Grid>
                                )}

                                <Title order={4} mt="md">Monitoring Configuration</Title>
                                <Checkbox
                                    label="Enable Monitoring"
                                    checked={form.values.monitoring?.enabled || false}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('monitoring.enabled', e.currentTarget.checked)}
                                />

                                {form.values.monitoring?.enabled && (
                                    <>
                                        <TextInput
                                            label="Metrics Endpoint"
                                            placeholder="/metrics"
                                            value={form.values.monitoring.metricsEndpoint || ''}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('monitoring.metricsEndpoint', e.currentTarget.value)}
                                        />

                                        <Title order={5} mt="md">Alert Thresholds (%)</Title>
                                        <Grid>
                                            <Grid.Col span={4}>
                                                <NumberInput
                                                    label="CPU Threshold"
                                                    min={0}
                                                    max={100}
                                                    value={form.values.monitoring.alertThresholds?.cpuPercent}
                                                    onChange={(value: string | number | undefined) => form.setFieldValue('monitoring.alertThresholds.cpuPercent', value)}
                                                />
                                            </Grid.Col>
                                            <Grid.Col span={4}>
                                                <NumberInput
                                                    label="Memory Threshold"
                                                    min={0}
                                                    max={100}
                                                    value={form.values.monitoring.alertThresholds?.memoryPercent}
                                                    onChange={(value: string | number | undefined) => form.setFieldValue('monitoring.alertThresholds.memoryPercent', value)}
                                                />
                                            </Grid.Col>
                                            <Grid.Col span={4}>
                                                <NumberInput
                                                    label="Storage Threshold"
                                                    min={0}
                                                    max={100}
                                                    value={form.values.monitoring.alertThresholds?.storagePercent}
                                                    onChange={(value: string | number | undefined) => form.setFieldValue('monitoring.alertThresholds.storagePercent', value)}
                                                />
                                            </Grid.Col>
                                        </Grid>
                                    </>
                                )}

                                <JsonInput
                                    label="Additional Metadata (JSON)"
                                    placeholder='{ "department": "engineering", "owner": "platform-team" }'
                                    validationError="Invalid JSON"
                                    formatOnBlur
                                    autosize
                                    minRows={4}
                                    maxRows={8}
                                    value={typeof form.values.metadata === 'string' ? 
                                        form.values.metadata : 
                                        JSON.stringify(form.values.metadata || {}, null, 2)
                                    }
                                    onChange={(value) => {
                                        try {
                                            const parsed = JSON.parse(value || '{}');
                                            form.setFieldValue('metadata', parsed);
                                        } catch {
                                            form.setFieldValue('metadata', value);
                                        }
                                    }}
                                    error={form.getFieldError('metadata')}
                                />
                            </Stack>
                        </Card>
                    </Tabs.Panel>
                </Tabs>

                {/* Form Actions */}
                <Paper p="md" withBorder>
                    <Group justify="space-between">
                        <div>
                            <Text size="sm" c="dimmed">
                                Resource will be validated before submission
                            </Text>
                            {form.isValidating && (
                                <Text size="sm" c="blue">
                                    Validating...
                                </Text>
                            )}
                        </div>
                        
                        <Group>
                            <Button variant="light" onClick={onCancel}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                loading={submitting || form.isValidating}
                                disabled={!form.validationResult.valid}
                            >
                                {mode === 'create' ? 'Create Resource' : 'Update Resource'}
                            </Button>
                        </Group>
                    </Group>
                </Paper>

                {/* Submission Result */}
                {submissionResult && (
                    <Alert 
                        color={submissionResult.success ? 'green' : 'red'}
                        title={submissionResult.success ? 'Success' : 'Error'}
                    >
                        {submissionResult.success ? 
                            'Resource saved successfully!' : 
                            submissionResult.error
                        }
                    </Alert>
                )}
            </Stack>
        </form>
    );
};

export default ValidatedResourceForm;